/* GASSISA
 =========================================================================================
 * Descripcion: Impresion del ticket de ventas. Un solo generador, calcado al
 *              metodo original handleImprimirTicketEscapeCommand:
 *
 *                - ESC @ / ESC K para encimar el encabezado sobre el logo.
 *                - Encabezado y pie con la alineacion del esquema.
 *                - Datos, detalle y totales desde ticketLayout.
 *                - X absoluta por objeto; la Y solo agrupa renglones.
 *                - Sin ancho de campo: el texto se imprime completo.
 *                - Si una linea excede los 96 caracteres de transporte, se
 *                  continua en la siguiente conservando la X; el ancho fisico
 *                  final lo ajusta la impresora.
 *
 *              El logo se envia aparte en el FormData y se escala con la
 *              configuracion del esquema (layout.logo.ancho/alto).
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 17/10/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion
 *      2       | 20/10/2026 | Lic. Jose Luis Aragon Cervantes  | Motor por objetos
 *      3       | 21/10/2026 | Lic. Jose Luis Aragon Cervantes  | Salida directa estilo EscapeCommand
 * ---------------------------------------------------------------------------------------*/

import {
    DOTS_POR_MM,
    MAX_COLUMNAS_LINEA,
    MAX_LOGO_WIDTH_DOTS,
    MAX_BYTE_SEGURO,
    MAX_AVANCE_DOTS,
    FONT_A_WIDTH_DOTS,
    FONT_B_WIDTH_DOTS,
    calcularColumnas,
    multiplicadorFuenteTipo,
} from "./ticketPerfil";
import {
    resolveTicketLayout,
    layoutTieneObjetos,
    agruparObjetosPorRenglon,
} from "./ticketMotorLayout";
import {
    comandoSeccion,
    renderFilasEsquema,
    datosFallback,
    detalleFallback,
    totalesFallback,
    concatenarPartes,
    RESET_ESTADO_ESTILOS,
} from "./ticketEscPos";
import {
    valorCampoDatos,
    valorCampoDetalle,
    valorCampoTotales,
    transliterarObjeto,
} from "./ticketLayoutCatalogs";

export const PRINTERTOOLS_TICKET_URL = "http://localhost:4000/api/imprimirticket";

export const DOTS_PER_MM = DOTS_POR_MM;
export { MAX_COLUMNAS_LINEA, MAX_LOGO_WIDTH_DOTS, MAX_BYTE_SEGURO, MAX_AVANCE_DOTS };
export { FONT_A_WIDTH_DOTS, FONT_B_WIDTH_DOTS };
export { resolveTicketLayout, layoutTieneObjetos, calcularColumnas, multiplicadorFuenteTipo, concatenarPartes };

const reemplazarEtiquetaRemision = (texto, etiquetaFolio) => {
    if (!etiquetaFolio) return texto;
    return String(texto ?? "")
        .replace(/Remisi[oó]n/gi, etiquetaFolio)
        .replace(/Folio\s+venta/gi, `Folio ${etiquetaFolio}`);
};

export const normalizarDataTicketCotizacion = ({ dataTicket, folioCotizacion, caja, parametrosCajaForm }) => {
    const documento = dataTicket || {};
    const cabecero = documento.cabecero || {};
    const detalles = (documento.detalles || []).map((item) => {
        const cantidad = Number(item.cantidad) || 0;
        const costoUnitario = Number(item.costounitario ?? item.costocliente) || 0;
        return {
            ...item,
            cantidad,
            costounitario: costoUnitario,
            descuento: Number(item.descuento ?? item.montodescuentoarticulo) || 0,
            total: Number(item.total) || (cantidad * costoUnitario),
            vendedorpreventa: item.vendedorpreventa || cabecero.usuario || "",
            almacenexhibidor: item.almacenexhibidor || "",
        };
    });

    const subtotal = Number(cabecero.subtotal) || detalles.reduce((suma, item) => suma + (item.cantidad * item.costounitario), 0);
    const iva = Number(cabecero.iva) || detalles.reduce((suma, item) => suma + (Number(item.iva) || 0), 0);
    const total = Number(cabecero.total) || detalles.reduce((suma, item) => suma + item.total, 0);

    return {
        ...documento,
        encabezado: documento.encabezado || parametrosCajaForm?.encabezadoticket || "",
        cabecero: {
            ...cabecero,
            pc: cabecero.pc || caja,
            remision: cabecero.remision || folioCotizacion,
            fechaventa: cabecero.fechaventa || cabecero.fecharegistro || "",
            razonsocial: cabecero.razonsocial || "No especificado",
            tipopago: cabecero.tipopago || "COTIZACION",
            subtotal,
            iva,
            total,
        },
        detalles,
    };
};

const filasDeSeccion = (objetos, textoDe, etiquetaFolio = null) => agruparObjetosPorRenglon(objetos).map((grupo) => ({
    items: grupo.objetos.map((objeto) => {
        const texto = objeto.tipo === "campo" ? textoDe(objeto.campo, objeto) : (objeto.texto ?? "");
        return {
            objeto,
            texto: objeto.tipo === "label" ? reemplazarEtiquetaRemision(texto, etiquetaFolio) : texto,
        };
    }),
}));

export const buildTicketVentaParts = ({
    dataTicket,
    parametrosCajaForm,
    esReimpresion = false,
    tipoDocumento = "VENTA",
    etiquetaFolio = null,
    tituloDocumento = "",
    despedida = "Vuelva pronto",
    lineasExtra = [],
    pie = null,
    reimpresionEtiqueta = null,
}) => {
    const layout = resolveTicketLayout(parametrosCajaForm);
    const textEncoder = new TextEncoder();
    const data = transliterarObjeto(dataTicket || {});
    const cabecero = data.cabecero || {};
    const dotsPorMm = layout.calibracion?.dotsPorMm || DOTS_POR_MM;
    const anchoCaracterDots = layout.calibracion?.anchoCaracterDots || 9;
    const etiquetaFolioFinal = etiquetaFolio ?? (
        tipoDocumento === "COTIZACION" ? "Cotizacion" :
        tipoDocumento === "APARTADO" ? "Apartado" : null
    );

    const encabezado = comandoSeccion(textEncoder, layout.cabecero, data.encabezado || "");

    const partes = [
        textEncoder.encode("\x1B\x40"),
        textEncoder.encode("\x1B\x4B\x60"),
    ];

    if (!encabezado.multilinea) {
        partes.push(textEncoder.encode("\x1B\x33\x00"));
    }

    partes.push(encabezado.bytes);

    if (encabezado.requiereReset) {
        partes.push(textEncoder.encode(RESET_ESTADO_ESTILOS));
    }

    if (!encabezado.multilinea) {
        partes.push(textEncoder.encode("\x1B\x32"));
    }

    if (tituloDocumento) {
        partes.push(textEncoder.encode("\x1B\x61\x01\x1B\x45\x01" + tituloDocumento + "\x1B\x45\x00\n"));
    }

    if (layout.datos.objetos.length > 0) {
        partes.push(...renderFilasEsquema({
            textEncoder,
            filas: filasDeSeccion(layout.datos.objetos, (campo) => valorCampoDatos(campo, cabecero), etiquetaFolioFinal),
            dotsPorMm,
            anchoCaracterDots,
        }));
    } else {
        partes.push(...datosFallback(textEncoder, cabecero, { etiquetaFolio: etiquetaFolioFinal }));
    }

    (data.detalles || []).forEach((datos) => {
        if (layout.detalle.objetos.length > 0) {
            partes.push(...renderFilasEsquema({
                textEncoder,
                filas: filasDeSeccion(layout.detalle.objetos, (campo) => valorCampoDetalle(campo, datos), etiquetaFolioFinal),
                dotsPorMm,
                anchoCaracterDots,
            }));
        } else {
            partes.push(...detalleFallback(textEncoder, datos, { tipoDocumento }));
        }
    });

    if (layout.totales.objetos.length > 0) {
        partes.push(...renderFilasEsquema({
            textEncoder,
            filas: filasDeSeccion(layout.totales.objetos, (campo) => valorCampoTotales(campo, cabecero, { tipoDocumento: tipoDocumento }), etiquetaFolioFinal),
            dotsPorMm,
            anchoCaracterDots,
        }));
    } else {
        partes.push(...totalesFallback(textEncoder, cabecero, { tipoDocumento }));
    }

    (lineasExtra || []).forEach((linea) => {
        partes.push(textEncoder.encode("\x1B\x61\x00" + linea + "\n"));
    });

    const pieConfigurado = pie ?? parametrosCajaForm?.pieticket ?? "";
    if (pieConfigurado.length > 0) {
        const pieSeccion = comandoSeccion(textEncoder, layout.pie, pieConfigurado);
        partes.push(pieSeccion.bytes);
        if (pieSeccion.requiereReset) {
            partes.push(textEncoder.encode(RESET_ESTADO_ESTILOS + "\n"));
        }
    }

    if (esReimpresion) {
        const etiqueta = reimpresionEtiqueta ?? (
            tipoDocumento === "COTIZACION" ? "REIMPRESION COTIZACION" :
            tipoDocumento === "APARTADO" ? "REIMPRESION APARTADO" : "REIMPRESION VENTA"
        );
        partes.push(textEncoder.encode("\x1B\x61\x01" + etiqueta + "\n"));
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x01" + despedida + "\n\n\n\n\n"),
        textEncoder.encode("\x1D\x56\x00")
    );

    return { partes };
};

export const escalarLogoImpresion = (logoBase64, anchoMm, altoMm) => {
    return new Promise((resolve, reject) => {
        if (!logoBase64) {
            resolve(null);
            return;
        }

        const anchoDots = Math.min(
            Math.max(1, Math.round((Number(anchoMm) || 0) * DOTS_POR_MM)),
            MAX_LOGO_WIDTH_DOTS
        );
        const altoDots = (Number(altoMm) || 0) > 0 ? Math.round(Number(altoMm) * DOTS_POR_MM) : null;

        const img = new Image();
        img.onload = () => {
            try {
                const w = anchoDots;
                const h = altoDots ?? Math.max(1, Math.round(img.height * (w / img.width)));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                const imageData = ctx.getImageData(0, 0, w, h);
                for (let i = 0; i < imageData.data.length; i += 4) {
                    const gray = 0.299 * imageData.data[i] + 0.587 * imageData.data[i + 1] + 0.114 * imageData.data[i + 2];
                    const bw = gray > 128 ? 255 : 0;
                    imageData.data[i] = bw;
                    imageData.data[i + 1] = bw;
                    imageData.data[i + 2] = bw;
                }
                ctx.putImageData(imageData, 0, 0);
                resolve(canvas.toDataURL("image/png").split(",")[1]);
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = () => reject(new Error("No fue posible cargar el logo para escalarlo"));
        img.src = `data:image/png;base64,${logoBase64}`;
    });
};

export const buildTicketVentaFormData = async ({ partes, parametrosCajaForm }) => {
    const layout = resolveTicketLayout(parametrosCajaForm);
    const formData = new FormData();
    formData.append("ticket", new Blob([concatenarPartes(partes)], { type: "application/octet-stream" }));
    formData.append("printerName", (parametrosCajaForm?.nombreimpresoraventas || "").trim());

    const logoBase64 = parametrosCajaForm?.logo;
    if (logoBase64) {
        let logoParaImpresion = logoBase64;
        if (layout.logo.ancho > 0) {
            try {
                logoParaImpresion = (await escalarLogoImpresion(logoBase64, layout.logo.ancho, layout.logo.alto)) || logoBase64;
            } catch {
                logoParaImpresion = logoBase64;
            }
        }
        const binary = atob(logoParaImpresion);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
    }

    return formData;
};
